// apps/web/src/features/kronos/timer/TimerRecoveryDialog.tsx
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { computeRecoveryEndTime, computeElapsedMs } from './timerMachine';
import type { TimerState } from './timerMachine';

interface TimerRecoveryDialogProps {
  open: boolean;
  state: TimerState;
  projectLabel: string;
  designationLabel: string;
  onResume: () => void;
  onSave: () => void;
  onDiscard: () => void;
}

function isSameDay(a: number, b: number): boolean {
  const da = new Date(a);
  const db = new Date(b);
  return da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate();
}

function formatCounter(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

export default function TimerRecoveryDialog({
  open,
  state,
  projectLabel,
  designationLabel,
  onResume,
  onSave,
  onDiscard,
}: TimerRecoveryDialogProps) {
  const { t } = useTranslation();
  const now = Date.now();
  const startedAt = Date.parse(state.startedAt);
  const canResume = isSameDay(startedAt, now) && state.status !== 'stopped';

  // Compute the recovery elapsed time
  const recoveryEnd = computeRecoveryEndTime(state, now);
  const elapsedMs = computeElapsedMs(state, recoveryEnd);
  const wasCapped = !isSameDay(startedAt, now) && !state.autoStopAt;

  const startDate = new Date(startedAt);
  const dateStr = startDate.toLocaleDateString();
  const timeStr = startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>{t('kronos.timer.recovery.title')}</DialogTitle>
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 1 }}>
          {t('kronos.timer.recovery.description', {
            date: dateStr,
            time: timeStr,
            project: projectLabel,
            duration: formatCounter(elapsedMs),
          })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {designationLabel}
        </Typography>
        {wasCapped && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
            {t('kronos.timer.recovery.cappedAtEndOfDay')}
          </Typography>
        )}
        <Box sx={{ mt: 2 }}>
          <Typography variant="h3" sx={{ fontWeight: 900, fontVariantNumeric: 'tabular-nums' }}>
            {formatCounter(elapsedMs)}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onDiscard} color="secondary" variant="outlined">
          {t('kronos.timer.recovery.discard')}
        </Button>
        {canResume && (
          <Button onClick={onResume}>
            {t('kronos.timer.recovery.resume')}
          </Button>
        )}
        <Button onClick={onSave} variant="contained" color="primary">
          {t('kronos.timer.recovery.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
